/**
 * src/config/generatorCatalogueView.js
 *
 * Pure selection logic for the Standalone Generators catalogue (client-side).
 *
 * Pagination model mirrors the DCC command centre PATTERN: one page at a time, no infinite scroll,
 * an "n of total" indicator with Previous/Next, and a reset to page 1 whenever the search, category,
 * sort or filters change. The DCC uses a fixed 8 because its grid is a 4x2 fit-to-window layout that
 * never scrolls; this catalogue is a scrollable web grid that is five cards per row on desktop, so
 * the base page size is fifteen cards. The page supplies the responsive whole-row capacity for the
 * active CSS breakpoint so a four-column viewport does not systematically render 15 cards and leave
 * its sixteenth slot empty. Only the current page is rendered, so a catalogue of hundreds still
 * mounts a bounded number of cards.
 *
 * These functions invent nothing: search matches only fields the catalogue item actually carries,
 * case-insensitively, by plain substring — no ranking, no semantic/AI matching, no fabricated order.
 */

export const SG_PER_PAGE = 15;

// These boundaries mirror StandaloneGeneratorsPage's .sg-grid media queries exactly.
export function generatorColumnsForWidth(width) {
  const viewport = Number.isFinite(width) ? width : Number.POSITIVE_INFINITY;
  if (viewport <= 600) return 1;
  if (viewport <= 860) return 2;
  if (viewport <= 1180) return 3;
  if (viewport <= 1500) return 4;
  return 5;
}

// Round the established 15-card target up to the next complete row at the active breakpoint.
// This is 15 for 5/3/1 columns and 16 for 4/2 columns.
export function generatorPageSizeForWidth(width) {
  const columns = generatorColumnsForWidth(width);
  return Math.ceil(SG_PER_PAGE / columns) * columns;
}

// Case-insensitive partial-match search over the item's real discovery metadata. Any field the
// catalogue does not carry is simply absent from the haystack; nothing is invented.
export function generatorMatchesQuery(item, query) {
  const needle = String(query == null ? '' : query).trim().toLowerCase();
  if (!needle) return true;
  const parts = [
    item.name, item.description, item.category, item.keywords || '',
    item.family || '', item.section || '',
    ...(Array.isArray(item.capabilities) ? item.capabilities : []),
    ...(Array.isArray(item.tags) ? item.tags : []),
    ...(Array.isArray(item.aliases) ? item.aliases : []),
  ];
  return parts.join(' ').toLowerCase().includes(needle);
}

// Sort the grid by a real, non-invented order: featured = the catalogue's own order (unchanged),
// az = by name, category = by category then name. No "newest" is offered because the catalogue
// carries no release-date field and inventing one would be a fabricated ordering.
export function sortGenerators(list, sort) {
  if (sort === 'az') {
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }
  if (sort === 'category') {
    return [...list].sort((a, b) =>
      (a.category || '').localeCompare(b.category || '') || (a.name || '').localeCompare(b.name || ''));
  }
  return list; // 'featured' / default: catalogue order preserved
}

// Clamp a requested page to the real range and return only that page's slice.
export function paginate(list, page, perPage = SG_PER_PAGE) {
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(Math.max(1, page || 1), pageCount);
  const start = (currentPage - 1) * perPage;
  return { total, pageCount, currentPage, pageItems: list.slice(start, start + perPage) };
}

/**
 * Filter -> sort -> paginate. `predicate` decides membership (category + search + owned/available
 * toggles) and is injected by the page so the same predicate governs the featured strip and the grid.
 * `excludeSlugs` keeps featured products out of the grid.
 */
export function selectGeneratorGrid({
  items = [],
  excludeSlugs = new Set(),
  predicate = () => true,
  sort = 'featured',
  page = 1,
  perPage = SG_PER_PAGE,
}) {
  const filtered = items.filter((it) => !excludeSlugs.has(it.slug) && predicate(it));
  const sorted = sortGenerators(filtered, sort);
  const { total, pageCount, currentPage, pageItems } = paginate(sorted, page, perPage);
  return { gridItems: sorted, total, pageCount, currentPage, pageItems };
}
